/**
 * /try Client API (Ingest + Polling Orchestrator).
 *
 * WHAT THIS FILE DOES:
 * - Exposes a small API for URL/text/PDF/media ingest.
 * - Runs /try media ingest through a client-audio-first upload strategy.
 * - Polls backend status endpoints for media extraction + summary readiness.
 * - Creates public shares once processing completes.
 * - Wraps fetch with auth + protocol headers.
 *
 * WHY THIS EXISTS:
 * - Keeps ingest orchestration separate from auth and UI code.
 * - Enables direct console testing via window.gistTry.* methods.
 * - Provides a single place to enforce backend API contract usage.
 *
 * CALLED BY:
 * - src/client/try.ts (entry point)
 * - src/client/try-ui.ts (UI controller)
 *
 * THIS FILE CALLS:
 * - WebTryAuthController for token management
 * - try-client-ingest.ts / try-client-transport.ts (extracted helpers)
 * - /api/web/ingest/* endpoints
 * - /api/media/upload/* endpoints
 * - /api/extension/check for readiness polling
 * - /api/share for public URLs
 *
 * ERROR HANDLING:
 * - Non-OK responses are converted to WebTryError with stable codes.
 * - Polling timeouts throw WebTryError('poll_timeout').
 *
 * STATE NOTES:
 * - authController is the only mutable dependency.
 * - init() memoizes to avoid repeated Firebase initialization.
 * - Several members are intentionally public (not `private`) because the
 *   extracted helpers in try-client-ingest.ts call back through the instance
 *   and unit tests stub them directly.
 *
 * DESIGN PRINCIPLES:
 * - Throw WebTryError for stable error handling.
 * - Keep request payloads explicit and minimal.
 * - Avoid direct DOM access (UI lives elsewhere).
 *
 * NON-GOALS:
 * - Rendering or DOM state (handled by try-ui.ts).
 * - Auth synchronization details (handled by try-auth.ts).
 *
 * @file src/client/try-client.ts
 */

import { WebTryAuthController } from './try-auth.js';
import { transcodeTryMediaClientSide } from './try-media-transcode.js';
import {
  PollingConfig,
  ProtocolHeaders,
  WebTryError
} from './try-shared.js';
import type {
  AuthSnapshot,
  IngestResponse,
  MediaUploadStatus,
  ShareResponse,
  TryFileUploadProgress,
  TryMediaClientTranscodeTelemetry,
  TryMediaIngestPhaseEvent
} from './try-shared.js';
import {
  delay,
  pollWithTimeout,
  resolveMediaContentType,
  uploadFileToSignedUrl
} from './try-utils.js';
import {
  FetchDefaults,
  ShareCreateConfig,
  awaitWithFetchAbort,
  normalizeFetchTransportError,
  withDefaultFetchTimeout,
  type TryMediaUploadResult
} from './try-client-transport.js';
import {
  createShareRequestWithRetry,
  initAndUploadDocument,
  initAndUploadMediaClientFirst,
  initAndUploadPdf,
  storeMediaUploadWsCredentials,
  submitUrlIngest,
  takeMediaUploadWsCredentials,
  waitForMediaReadyViaWs
} from './try-client-ingest.js';

/**
 * Primary /try client API.
 */
export class WebTryClient {
  authController = new WebTryAuthController();
  private initPromise: Promise<void> | null = null;
  readonly shareCreateConfig = ShareCreateConfig;
  transcodeMediaClientSide = transcodeTryMediaClientSide;
  getNowMs = (): number => Date.now();

  /**
   * SPEED OPTIMIZATION (try-speed-plan Phase 3a):
   * Stashed WS credentials from media upload init for push-based readiness.
   * Keyed by uploadId. Consumed once by waitForMediaReady().
   */
  mediaUploadWsCredentials = new Map<string, { uploadWsUrl: string; uploadWsToken: string }>();
  mediaUploadWsCredentialCleanupTimers = new Map<string, ReturnType<typeof setTimeout>>();

  /**
   * Initializes auth + bridge listeners.
   */
  init(): Promise<void> {
    if (!this.initPromise) {
      this.initPromise = this.authController.init();
    }
    return this.initPromise;
  }

  /**
   * Returns the current auth snapshot.
   */
  getAuthSnapshot(): AuthSnapshot {
    return this.authController.getSnapshot();
  }

  /**
   * Subscribes to auth state changes.
   */
  onAuthStateChange(handler: (snapshot: AuthSnapshot) => void): () => void {
    return this.authController.subscribe(handler);
  }

  /**
   * Signs out of Firebase and extension sessions.
   */
  async logout(): Promise<void> {
    await this.authController.logout();
  }

  /**
   * Signs in with Google via Firebase popup.
   */
  async signInWithGoogle(): Promise<void> {
    await this.init();
    await this.authController.signInWithGoogle();
  }

  /**
   * Signs in with email/password via Firebase.
   */
  async signInWithEmail(email: string, password: string): Promise<void> {
    await this.init();
    await this.authController.signInWithEmail(email, password);
  }

  /**
   * Ingests a URL via the web ingest endpoint.
   */
  async ingestUrl(params: {
    url: string;
    gistLanguage?: string;
    createShare?: boolean;
    forceRefresh?: boolean;
  }): Promise<IngestResponse> {
    await this.init();
    return await submitUrlIngest(this, {
      url: params.url,
      gistLanguage: params.gistLanguage,
      createShare: params.createShare,
      forceRefresh: params.forceRefresh
    });
  }

  /**
   * Forces URL re-ingest by bypassing the cache gate (`?force=true`).
   *
   * This is used by `/try` regenerate-style actions when the user explicitly
   * wants a fresh scrape instead of serving cached ready content.
   */
  async regenerateUrl(params: {
    url: string;
    gistLanguage?: string;
    createShare?: boolean;
  }): Promise<IngestResponse> {
    await this.init();
    return await submitUrlIngest(this, {
      url: params.url,
      gistLanguage: params.gistLanguage,
      createShare: params.createShare,
      forceRefresh: true
    });
  }

  /**
   * Speculatively ingests a URL (abortable).
   *
   * This is used by /try speculation to start scrape + commit before submit.
   * `speculativeInstantOnly` defaults to true so speculative URL requests always
   * carry explicit "Instant-only" intent.
   */
  async speculativeIngestUrl(params: {
    url: string;
    gistLanguage?: string;
    signal?: AbortSignal;
    createShare?: boolean;
    forceRefresh?: boolean;
    speculativeInstantOnly?: boolean;
  }): Promise<IngestResponse> {
    await this.init();
    return await submitUrlIngest(this, {
      url: params.url,
      gistLanguage: params.gistLanguage,
      createShare: params.createShare,
      forceRefresh: params.forceRefresh,
      signal: params.signal,
      speculativeInstantOnly: params.speculativeInstantOnly ?? true
    });
  }

  /**
   * Ingests plain text via the web ingest text endpoint.
   */
  async ingestText(params: {
    text: string;
    gistLanguage?: string;
    title?: string;
    sourceFileName?: string;
    createShare?: boolean;
  }): Promise<IngestResponse> {
    await this.init();

    const trimmed = params.text.trim();
    if (!trimmed) {
      throw new WebTryError('text_required');
    }

    return await this.requestJson<IngestResponse>('/api/web/ingest/text', {
      method: 'POST',
      body: JSON.stringify({
        text: trimmed,
        gistLanguage: params.gistLanguage ?? undefined,
        title: params.title ?? undefined,
        sourceFileName: params.sourceFileName ?? undefined,
        ...(params.createShare ? { createShare: true } : {})
      })
    });
  }

  /**
   * Ingests a PDF by uploading then submitting to the PDF ingest endpoints.
   */
  async ingestPdf(params: {
    file: File;
    gistLanguage?: string;
    createShare?: boolean;
    onUploadProgress?: (progress: TryFileUploadProgress) => void;
  }): Promise<IngestResponse> {
    const upload = await this.initAndUploadPdf({ file: params.file, onUploadProgress: params.onUploadProgress });
    return await this.ingestPdfSubmitOnly({
      uploadId: upload.uploadId,
      fileName: upload.fileName,
      gistLanguage: params.gistLanguage,
      createShare: params.createShare
    });
  }

  /**
   * Ingests a Docling-supported document by uploading then submitting.
   */
  async ingestDocument(params: {
    file: File;
    gistLanguage?: string;
    createShare?: boolean;
    onUploadProgress?: (progress: TryFileUploadProgress) => void;
  }): Promise<IngestResponse> {
    const upload = await this.initAndUploadDocument({ file: params.file, onUploadProgress: params.onUploadProgress });
    return await this.ingestDocumentSubmitOnly({
      uploadId: upload.uploadId,
      fileName: upload.fileName,
      gistLanguage: params.gistLanguage,
      createShare: params.createShare
    });
  }

  /**
   * Ingests audio/video through client-audio-first upload, then submits.
   */
  async ingestMedia(params: {
    file: File;
    gistLanguage?: string;
    createShare?: boolean;
    durationMs?: number;
    onUploadProgress?: (progress: TryFileUploadProgress) => void;
    onPhaseChange?: (event: TryMediaIngestPhaseEvent) => void;
    onTranscodeDecision?: (telemetry: TryMediaClientTranscodeTelemetry) => void;
  }): Promise<IngestResponse> {
    const upload = await this.initAndUploadMediaClientFirst({
      file: params.file,
      durationMs: params.durationMs,
      onPhaseChange: params.onPhaseChange,
      onUploadProgress: params.onUploadProgress,
      onTranscodeDecision: params.onTranscodeDecision
    });

    return await this.ingestMediaSubmitOnly({
      uploadId: upload.uploadId,
      fileName: upload.fileName,
      gistLanguage: params.gistLanguage,
      createShare: params.createShare
    });
  }

  /**
   * Initializes a PDF upload and uploads the file to GCS.
   */
  async initAndUploadPdf(params: {
    file: File;
    signal?: AbortSignal;
    onUploadProgress?: (progress: TryFileUploadProgress) => void;
  }): Promise<{ uploadId: string; fileName: string; contentType: string }> {
    return await initAndUploadPdf(this, params);
  }

  /**
   * Initializes a document upload and uploads the file to GCS.
   */
  async initAndUploadDocument(params: {
    file: File;
    signal?: AbortSignal;
    onUploadProgress?: (progress: TryFileUploadProgress) => void;
  }): Promise<{ uploadId: string; fileName: string; contentType: string }> {
    return await initAndUploadDocument(this, params);
  }

  /**
   * Submits a previously uploaded PDF by uploadId.
   */
  async ingestPdfSubmitOnly(params: { uploadId: string; fileName?: string; gistLanguage?: string; createShare?: boolean }): Promise<IngestResponse> {
    await this.init();

    return await this.requestJson<IngestResponse>('/api/web/ingest/pdf/submit', {
      method: 'POST',
      body: JSON.stringify({
        uploadId: params.uploadId,
        fileName: params.fileName ?? undefined,
        gistLanguage: params.gistLanguage ?? undefined,
        ...(params.createShare ? { createShare: true } : {})
      })
    });
  }

  /**
   * Submits a previously uploaded document by uploadId.
   */
  async ingestDocumentSubmitOnly(params: { uploadId: string; fileName?: string; gistLanguage?: string; createShare?: boolean }): Promise<IngestResponse> {
    await this.init();

    return await this.requestJson<IngestResponse>('/api/web/ingest/document/submit', {
      method: 'POST',
      body: JSON.stringify({
        uploadId: params.uploadId,
        fileName: params.fileName ?? undefined,
        gistLanguage: params.gistLanguage ?? undefined,
        ...(params.createShare ? { createShare: true } : {})
      })
    });
  }

  /**
   * Speculatively extracts text from an uploaded document (cached server-side).
   */
  async extractDocument(params: { uploadId: string; signal?: AbortSignal }): Promise<{ status: string; uploadId: string }> {
    await this.init();

    return await this.requestJson<{ status: string; uploadId: string }>('/api/web/ingest/document/extract', {
      method: 'POST',
      body: JSON.stringify({
        uploadId: params.uploadId
      }),
      signal: params.signal
    });
  }

  /**
   * Speculatively extracts text from an uploaded PDF (cached server-side).
   */
  async extractPdf(params: { uploadId: string; signal?: AbortSignal }): Promise<{ status: string; uploadId: string }> {
    await this.init();

    return await this.requestJson<{ status: string; uploadId: string }>('/api/web/ingest/pdf/extract', {
      method: 'POST',
      body: JSON.stringify({
        uploadId: params.uploadId
      }),
      signal: params.signal
    });
  }

  /**
   * Uploads media using a client-audio-first strategy with server fallback.
   *
   * Called by:
   * - ingestMedia() for normal submit flow.
   * - try-speculation.ts runMediaSpeculation() for speculative media staging.
   */
  async initAndUploadMediaClientFirst(params: {
    file: File;
    durationMs?: number;
    signal?: AbortSignal;
    onUploadProgress?: (progress: TryFileUploadProgress) => void;
    onPhaseChange?: (event: TryMediaIngestPhaseEvent) => void;
    onTranscodeDecision?: (telemetry: TryMediaClientTranscodeTelemetry) => void;
  }): Promise<TryMediaUploadResult> {
    return await initAndUploadMediaClientFirst(this, params);
  }

  /**
   * Initializes a media upload and uploads the file to GCS.
   *
   * A-021: Wraps the upload call with a single retry on transient network
   * errors. Aborts are never retried. The retry re-uses the same signed URL
   * since it was just generated and has a 10-minute TTL.
   */
  async initAndUploadMedia(params: {
    file: File;
    durationMs?: number;
    signal?: AbortSignal;
    onUploadProgress?: (progress: TryFileUploadProgress) => void;
  }): Promise<{ uploadId: string; fileName: string; contentType: string }> {
    await this.init();

    const contentType = resolveMediaContentType(params.file);
    if (!contentType) {
      throw new WebTryError('invalid_media_type');
    }

    const initResponse = await this.requestJson<{
      uploadId: string;
      uploadUrl: string | null;
      uploadMode: string;
      uploadWsUrl?: string | null;
      uploadWsToken?: string | null;
    }>(
      '/api/media/upload/init',
      {
        method: 'POST',
        body: JSON.stringify({
          contentType,
          contentLength: params.file.size,
          fileName: params.file.name,
          durationMs: params.durationMs ?? undefined,
          uploadMode: 'signed'
        }),
        signal: params.signal
      }
    );

    if (!initResponse.uploadUrl) {
      throw new WebTryError('media_upload_url_missing');
    }

    // SPEED OPTIMIZATION (try-speed-plan Phase 3a):
    // Stash WS credentials so waitForMediaReady() can connect for push readiness.
    if (initResponse.uploadWsUrl && initResponse.uploadWsToken) {
      storeMediaUploadWsCredentials(this, initResponse.uploadId, {
        uploadWsUrl: initResponse.uploadWsUrl,
        uploadWsToken: initResponse.uploadWsToken
      });
    }

    // A-021: 1-retry on transient upload failure.
    try {
      await uploadFileToSignedUrl(initResponse.uploadUrl, params.file, {
        contentType,
        signal: params.signal,
        onProgress: params.onUploadProgress
      });
    } catch (uploadError) {
      // Never retry user-initiated aborts.
      if (uploadError instanceof DOMException && uploadError.name === 'AbortError') {
        throw uploadError;
      }
      // Wait 2 seconds and retry once.
      await delay(2_000);
      await uploadFileToSignedUrl(initResponse.uploadUrl, params.file, {
        contentType,
        signal: params.signal,
        onProgress: params.onUploadProgress
      });
    }

    return {
      uploadId: initResponse.uploadId,
      fileName: params.file.name,
      contentType
    };
  }

  /**
   * Submits a previously uploaded media file by uploadId.
   */
  async ingestMediaSubmitOnly(params: { uploadId: string; fileName?: string; gistLanguage?: string; createShare?: boolean }): Promise<IngestResponse> {
    await this.init();

    await this.waitForMediaReady(params.uploadId);

    return await this.requestJson<IngestResponse>('/api/web/ingest/media/submit', {
      method: 'POST',
      body: JSON.stringify({
        uploadId: params.uploadId,
        fileName: params.fileName ?? undefined,
        gistLanguage: params.gistLanguage ?? undefined,
        ...(params.createShare ? { createShare: true } : {})
      })
    });
  }

  /**
   * Creates one share, then polls until the backend says the content is ready.
   *
   * This keeps `/try` from issuing a second `/api/share` after readiness while
   * still preventing the UI from calling a pending share "Ready".
   */
  async createShareWhenReady(params: { contentId: string; gistLanguage?: string }): Promise<ShareResponse> {
    await this.init();

    const share = await createShareRequestWithRetry(this, params);
    if (share.isRenderable === true) {
      return share;
    }
    await this.waitForContentReady(params.contentId, params.gistLanguage ?? undefined);
    return share;
  }

  /**
   * Creates a share link immediately without waiting for content readiness.
   *
   * Called by:
   * - try-ui.ts handleSubmit() for the new-tab reader flow.
   *
   * Why this exists:
   * - The backend accepts shares where content is still processing (storyStreamR2Key
   *   can be null). The share page renders fallback content and auto-refreshes.
   * - This allows us to open the share URL in a new tab instantly, instead of
   *   waiting for the full summary pipeline to complete.
   *
   * @param params.contentId - The content ID returned from ingest.
   * @param params.gistLanguage - Optional language code.
   * @returns ShareResponse with publicUrl for the new tab.
   */
  async createShareInstant(params: { contentId: string; gistLanguage?: string }): Promise<ShareResponse> {
    await this.init();

    return await createShareRequestWithRetry(this, params);
  }

  /**
   * Waits for media extraction to complete, using WebSocket push with polling fallback.
   *
   * SPEED OPTIMIZATION (try-speed-plan Phase 3a):
   * - First attempts WebSocket push via MediaUploadGateway DO (near-instant delivery).
   * - Falls back to polling if WS connect fails, times out, or credentials unavailable.
   *
   * A-040: Tracks consecutive server errors (5xx) in polling mode. After 5 in a row,
   * throws instead of continuing to poll indefinitely.
   */
  async waitForMediaReady(uploadId: string): Promise<MediaUploadStatus> {
    await this.init();

    // Try WebSocket push first if credentials are available.
    const wsCredentials = takeMediaUploadWsCredentials(this, uploadId);
    if (wsCredentials) {
      try {
        const wsResult = await waitForMediaReadyViaWs(uploadId, wsCredentials);
        if (wsResult) {
          return wsResult;
        }
      } catch (wsError) {
        // Terminal errors (upload_error from DO) must propagate — don't fall through to polling.
        if (wsError instanceof WebTryError) {
          throw wsError;
        }
        // Non-terminal WS failures (network, parse) — fall through to polling.
      }
    }

    return await this.pollMediaUploadStatus(uploadId);
  }

  /**
   * Polls /api/media/upload/status until ready or error.
   *
   * A-040: Tracks consecutive server errors (5xx). After 5 in a row,
   * throws instead of continuing to poll indefinitely.
   */
  async pollMediaUploadStatus(uploadId: string): Promise<MediaUploadStatus> {
    const startedAt = Date.now();
    /** A-040: Consecutive 5xx counter — resets on any non-5xx response. */
    let consecutive500s = 0;

    return await pollWithTimeout(
      async () => {
        const response = await this.fetchWithAuth(
          `/api/media/upload/status?uploadId=${encodeURIComponent(uploadId)}`,
          { method: 'GET' }
        );

        // A-040: Track consecutive server errors.
        if (response.status >= 500) {
          consecutive500s += 1;
          if (consecutive500s >= 5) {
            throw new WebTryError('media_status_failed', { status: response.status });
          }
          return { done: false };
        }
        consecutive500s = 0;

        const status = await response.json().catch(() => null) as MediaUploadStatus | null;

        if (!status) {
          throw new WebTryError('media_status_invalid', { status: response.status });
        }

        if (status.status === 'ready') {
          return { done: true, value: status };
        }
        if (status.status === 'error') {
          throw new WebTryError(status.error ?? 'media_extraction_failed', { status: response.status, details: status });
        }
        if (status.status === 'missing') {
          const withinGrace = Date.now() - startedAt < PollingConfig.MEDIA_STATUS_MISSING_GRACE_MS;
          if (withinGrace) {
            return { done: false };
          }
          throw new WebTryError('media_upload_missing', { status: response.status, details: status });
        }
        if (!response.ok && status.status !== 'pending') {
          throw new WebTryError('media_status_failed', { status: response.status, details: status });
        }
        return { done: false };
      },
      {
        intervalMs: PollingConfig.MEDIA_STATUS_INTERVAL_MS,
        timeoutMs: PollingConfig.MEDIA_STATUS_TIMEOUT_MS
      }
    );
  }

  /**
   * Polls /api/extension/check until the content status is ready or failed.
   */
  async waitForContentReady(contentId: string, gistLanguage?: string): Promise<void> {
    await this.init();

    const query = new URLSearchParams({ contentId });
    if (gistLanguage) {
      query.set('language', gistLanguage);
    }

    await pollWithTimeout(
      async () => {
        const status = await this.requestJson<{ status?: string; exists?: boolean }>(
          `/api/extension/check?${query.toString()}`,
          { method: 'GET' }
        );

        if (status.status === 'ready') {
          return { done: true, value: null };
        }
        if (status.status === 'failed') {
          throw new WebTryError('content_failed');
        }
        if (status.exists === false) {
          throw new WebTryError('content_not_found');
        }
        return { done: false };
      },
      {
        intervalMs: PollingConfig.CONTENT_STATUS_INTERVAL_MS,
        timeoutMs: PollingConfig.CONTENT_STATUS_TIMEOUT_MS
      }
    );
  }

  /**
   * Fetches the full /api/extension/check payload for a contentId.
   *
   * Used by:
   * - try-ui.ts polling fallback when WebSocket streaming is unavailable.
   */
  async getContentStatus(params: { contentId: string; gistLanguage?: string }): Promise<Record<string, unknown>> {
    await this.init();

    const query = new URLSearchParams({ contentId: params.contentId });
    if (params.gistLanguage) {
      query.set('language', params.gistLanguage);
    }

    return await this.requestJson<Record<string, unknown>>(
      `/api/extension/check?${query.toString()}`,
      { method: 'GET' }
    );
  }

  /**
   * Resolves a stable authState label for telemetry payloads.
   */
  getAuthStateLabel(): string {
    const snapshot = this.getAuthSnapshot();
    if (snapshot.workerToken) {
      return 'extension';
    }
    if (snapshot.user && !snapshot.user.isAnonymous) {
      return 'firebase';
    }
    if (snapshot.mode === 'none') {
      return 'unknown';
    }
    return snapshot.mode;
  }

  /**
   * Performs a fetch with Authorization header attached.
   */
  async fetchWithAuth(input: string, init: RequestInit): Promise<Response> {
    const timeoutInit = withDefaultFetchTimeout(init, FetchDefaults.TIMEOUT_MS);
    const effectiveInit = timeoutInit.init;
    const signal = effectiveInit.signal ?? null;

    try {
      const applyStandardHeaders = (headers: Headers) => {
        if (!headers.has('X-Gist-Protocol-Version')) {
          headers.set('X-Gist-Protocol-Version', ProtocolHeaders.PROTOCOL_VERSION);
        }
        if (!headers.has('X-Gist-Client-Version')) {
          headers.set('X-Gist-Client-Version', ProtocolHeaders.CLIENT_VERSION);
        }
        // A-037: Ensure JSON body requests include the correct Content-Type.
        if (effectiveInit.body && !headers.has('Content-Type')) {
          headers.set('Content-Type', 'application/json');
        }
      };

      const attempt = async (authHeader: string | null): Promise<Response> => {
        const headers = new Headers(effectiveInit.headers ?? {});
        applyStandardHeaders(headers);
        if (authHeader) {
          headers.set('Authorization', authHeader);
        }

        try {
          return await fetch(input, {
            ...effectiveInit,
            credentials: authHeader ? effectiveInit.credentials : 'include',
            headers
          });
        } catch (error) {
          throw normalizeFetchTransportError(error, timeoutInit.ownsSignal);
        }
      };

      const authHeader = await awaitWithFetchAbort(
        () => this.authController.getAuthorizationHeader(),
        signal,
        timeoutInit.ownsSignal
      );
      const usedWebSessionAuth = !authHeader && this.authController.hasWebSessionAuth();
      if (!authHeader && !usedWebSessionAuth) {
        const lastAuthError = this.authController.getLastAuthErrorCode();
        if (lastAuthError) {
          throw new WebTryError(lastAuthError);
        }
        throw new WebTryError('auth_required');
      }

      const response = await attempt(authHeader);

      if (usedWebSessionAuth && response.status === 401) {
        this.authController.clearWebSessionAuth('unauthorized');
        const fallbackHeader = await awaitWithFetchAbort(
          () => this.authController.getAuthorizationHeader({ allowAnonymous: false }),
          signal,
          timeoutInit.ownsSignal
        );
        if (!fallbackHeader) {
          return response;
        }
        return await attempt(fallbackHeader);
      }

      // If the extension session token is invalid (expired / wrong environment),
      // fall back to a Firebase ID token so /try stays usable.
      //
      // Important: the backend treats worker tokens as non-Firebase and will not
      // attempt Firebase verification when they fail. So the retry must happen
      // client-side.
      const usedWorkerToken = authHeader?.includes('gist-worker.') === true;
      if (usedWorkerToken && response.status === 401) {
        this.authController.clearWorkerToken('unauthorized');
        const fallbackHeader = await awaitWithFetchAbort(
          () => this.authController.getAuthorizationHeader(),
          signal,
          timeoutInit.ownsSignal
        );
        if (!fallbackHeader) {
          return response;
        }
        return await attempt(fallbackHeader);
      }

      return response;
    } finally {
      timeoutInit.cleanup();
    }
  }

  /**
   * Fetches JSON and throws on non-OK responses.
   *
   * Special-case: HTTP 402 from a paid-gated route is normalized to a stable
   * `payment_required` error code so the UI can route the user to the same
   * subscribe path as the extension. The backend contract is documented in
   * README/Plans/growth/170-paid-only-stripe-account-first-plan.md ("Paid-gate
   * denial uses HTTP 402"). We capture the body's `checkoutUrl` so the UI
   * can deep-link to whatever join path the backend currently advertises.
   */
  async requestJson<T>(input: string, init: RequestInit): Promise<T> {
    const response = await this.fetchWithAuth(input, init);
    const body = await response.json().catch(() => null) as T | null;

    if (response.status === 402) {
      const checkoutUrl = (body as { checkoutUrl?: string } | null)?.checkoutUrl ?? null;
      throw new WebTryError('payment_required', {
        status: 402,
        details: { checkoutUrl }
      });
    }

    if (!response.ok) {
      const errorMessage = (body as { error?: string } | null)?.error ?? 'request_failed';
      throw new WebTryError(errorMessage, { status: response.status, details: body });
    }

    if (!body) {
      throw new WebTryError('empty_response');
    }

    return body;
  }
}
